import React, { createContext, useContext, useMemo } from 'react'
import { useDataSource } from '../../../lib/dataSource'
import type { LifeSpace, Project, Task } from '../../../lib/db/models'

interface SpaceData {
  /** Projekte des Baums, ohne Systemprojekte, in ihrer Reihenfolge. */
  projects: Project[] | undefined
  /** Alle lebenden Projekte samt Systemprojekten — zum Zuordnen von Favoriten-Aufgaben. */
  spaceProjects: Project[] | undefined
  /** Zahl am Eintrag „Demnächst“; `0`, solange sie zum allerersten Mal lädt. */
  upcomingCount: number
  /** Ob Kurz notiert etwas Offenes enthält (gefülltes oder leeres Symbol). */
  quickNotesHasEntries: boolean
}

interface SidebarData {
  spaces: Record<LifeSpace, SpaceData>
  favoriteTasks: Task[] | undefined
}

const SidebarDataContext = createContext<SidebarData | null>(null)

/**
 * **Hält die Daten der Sidebar oberhalb des Wechsels des Lebensbereichs** (#285). Der Inhalt hängt
 * beim Wechsel neu ein (`key` in `StandardSidebar`); fragte jede Gruppe selbst ab, begänne jede
 * Abfrage bei `undefined` — leere Gruppe, fehlende Favoriten, dann Einfahren. Hier laufen die
 * Abfragen aller drei Lebensbereiche (Projekte, Zahl bei „Demnächst“, Kurz-notiert-Symbol) und die
 * Favoriten-Aufgaben dauerhaft; die Gruppen lesen nur.
 *
 * Feste Liste statt `lifeSpace`-Abhängigkeit: Ein `useLiveQuery` mit wechselnden `deps` zeigte
 * sonst kurz das Ergebnis des vorigen Lebensbereichs.
 */
export const SidebarDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const source = useDataSource()
  // Drei feste Aufrufe je Hook, für die drei Lebensbereiche, die ein Projekt tragen können
  // (CONTEXT.md → Lebensbereich); „Alle“ ist eine Sicht ohne eigene Daten.
  const personalProjects = source.useProjectsByLifeSpace('personal')
  const workProjects = source.useProjectsByLifeSpace('work')
  const homeProjects = source.useProjectsByLifeSpace('home')
  const personalSpaceProjects = source.useLifeSpaceProjects('personal')
  const workSpaceProjects = source.useLifeSpaceProjects('work')
  const homeSpaceProjects = source.useLifeSpaceProjects('home')
  // Zahl bei „Demnächst“ und Kurz-notiert-Symbol aller drei Bereiche: eine Abfrage statt sechs (#309).
  const counts = source.useSidebarCounts()
  // Die Favoriten-Aufgaben sind global; die Sidebar steht immer da, die Abfrage pausiert nie.
  const favoriteTasks = source.useFavoriteTasks(true)

  const value = useMemo<SidebarData>(() => ({
    spaces: {
      personal: { projects: personalProjects, spaceProjects: personalSpaceProjects, ...counts.personal },
      work: { projects: workProjects, spaceProjects: workSpaceProjects, ...counts.work },
      home: { projects: homeProjects, spaceProjects: homeSpaceProjects, ...counts.home },
    },
    favoriteTasks,
  }), [
    personalProjects, workProjects, homeProjects,
    personalSpaceProjects, workSpaceProjects, homeSpaceProjects,
    counts,
    favoriteTasks,
  ])

  return <SidebarDataContext.Provider value={value}>{children}</SidebarDataContext.Provider>
}

/**
 * Projekte, Projekte samt Systemprojekten, Favoriten-Aufgaben, Zahl bei „Demnächst“ und Kurz-notiert-
 * Symbol für einen Lebensbereich — Listen `undefined`, Zahl `0` und Symbol `false`, solange sie zum
 * allerersten Mal laden. Wirft ohne `SidebarDataProvider`: Eine
 * stille Doppel-Abfrage wäre der Fehler, den es zu vermeiden gilt.
 */
export function useSidebarData(lifeSpace: string): SpaceData & { favoriteTasks: Task[] | undefined } {
  const data = useContext(SidebarDataContext)
  if (!data) throw new Error('useSidebarData braucht einen SidebarDataProvider (StandardSidebar stellt ihn).')
  const space = data.spaces[lifeSpace as LifeSpace] ?? { projects: undefined, spaceProjects: undefined, upcomingCount: 0, quickNotesHasEntries: false }
  return { ...space, favoriteTasks: data.favoriteTasks }
}
